import { useState } from 'react';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AuthUser } from 'shared';
import { api } from '../api';
import { LabeledField } from '../components/LabeledField';
import { Note } from '../components/Note';
import { useLogout } from '../auth';
import { DISPLAY, tokens } from '../theme';

/** Trùng ràng buộc ở `packages/shared/src/auth.ts` — server vẫn là nơi kiểm thật. */
const MIN_PASSWORD = 10;

/**
 * Màn bắt buộc đổi mật khẩu — spec SC-09-C.
 *
 * Hiện THAY CHO toàn bộ ứng dụng khi người dùng đang đăng nhập bằng mật khẩu tạm
 * do quản trị đặt hộ: không thanh điều hướng, không đường vòng. Đây chỉ là lớp
 * thuận tiện — API cũng chặn mọi đường khác bằng guard, nên gọi thẳng API cũng
 * không đi được đâu.
 *
 * Vẫn để nút Đăng xuất: nhốt người dùng trong một màn không lối ra là sai, kể cả
 * khi màn đó chỉ có một việc phải làm.
 */
export function MustChangePasswordPage({ user }: { user: AuthUser }) {
  const qc = useQueryClient();
  const logout = useLogout();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const doi = useMutation({
    mutationFn: () => api.changePassword({ currentPassword: current, newPassword: next }),
    // Đổi xong thì đọc lại `me`: cờ `mustChangePassword` tắt đi và ứng dụng tự
    // hiện ra bình thường, không phải tải lại trang.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });

  const lech = confirm.length > 0 && next !== confirm;
  const quaNgan = next.length > 0 && next.length < MIN_PASSWORD;
  const guiDuoc =
    current.length > 0 && next.length >= MIN_PASSWORD && next === confirm && !doi.isPending;

  return (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100dvh', p: 3 }}>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 3.5 }, width: '100%', maxWidth: 440 }}>
        <Typography sx={{ fontSize: 11.5, letterSpacing: '.14em', color: tokens.teal, fontWeight: 600 }}>
          BƯỚC BẮT BUỘC
        </Typography>
        <Typography sx={{ fontFamily: DISPLAY, fontSize: 22, fontWeight: 600, color: tokens.ink, mt: 0.5 }}>
          Đặt mật khẩu của riêng bạn
        </Typography>

        <Box sx={{ mt: 2, mb: 2.5 }}>
          <Note tone="warn">
            Bạn đang dùng <strong>mật khẩu tạm</strong> do quản trị đặt hộ. Người đặt nó biết mật
            khẩu này, và nó hết hạn sau 24 giờ — hãy đổi ngay để dùng tiếp.
          </Note>
        </Box>

        <Stack
          component="form"
          spacing={2}
          onSubmit={(e) => {
            e.preventDefault();
            if (guiDuoc) doi.mutate();
          }}
        >
          <LabeledField
            label="Mật khẩu tạm"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
          <LabeledField
            label="Mật khẩu mới"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            error={quaNgan}
            helperText={quaNgan ? `Cần từ ${MIN_PASSWORD} ký tự` : `Tối thiểu ${MIN_PASSWORD} ký tự`}
          />
          <LabeledField
            label="Nhập lại mật khẩu mới"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={lech}
            helperText={lech ? 'Hai lần nhập không giống nhau' : undefined}
          />

          {doi.isError && <Note tone="bad">{(doi.error as Error).message}</Note>}

          <Button
            type="submit"
            variant="contained"
            disabled={!guiDuoc}
            startIcon={doi.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {doi.isPending ? 'Đang đổi…' : 'Đổi mật khẩu và vào hệ thống'}
          </Button>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{user.email}</Typography>
            <Button size="small" color="inherit" onClick={() => logout.mutate()}>
              Đăng xuất
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
}
